/**
 * Version History Tab Component
 * Displays all versions of a domain pack with details
 *
 * Phase 1 Implementation (November 2025)
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Clock, Users, Package, CheckCircle2, AlertCircle, Loader2, ChevronDown, ChevronUp } from 'lucide-react';

import { listDomainPackVersions, getDomainPackVersion } from '../../lib/api/domainPacksApi';
import { Card, CardHeader, CardBody } from '../ui/Card';
import { Badge } from '../ui/Badge';
import Button from '../ui/Button';

interface VersionHistoryTabProps {
  domainPackId: string;
}

export const VersionHistoryTab: React.FC<VersionHistoryTabProps> = ({ domainPackId }) => {
  const [expandedVersion, setExpandedVersion] = useState<string | null>(null);

  // Fetch version list
  const { data: versionList, isLoading, error } = useQuery({
    queryKey: ['domain-pack-versions', domainPackId],
    queryFn: () => listDomainPackVersions(domainPackId),
  });

  // Fetch expanded version details
  const { data: versionDetails } = useQuery({
    queryKey: ['domain-pack-version', domainPackId, expandedVersion],
    queryFn: () => getDomainPackVersion(domainPackId, expandedVersion!),
    enabled: !!expandedVersion,
  });

  const toggleVersion = (versionId: string) => {
    setExpandedVersion(expandedVersion === versionId ? null : versionId);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
        <span className="ml-3 text-neutral-600">Loading version history...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-red-500">
        <CardBody className="py-8 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-neutral-900 mb-2">Failed to Load Versions</h3>
          <p className="text-neutral-600">
            {error instanceof Error ? error.message : 'An error occurred while loading version history'}
          </p>
        </CardBody>
      </Card>
    );
  }

  if (!versionList || versionList.versions.length === 0) {
    return (
      <Card>
        <CardBody className="py-8 text-center">
          <Package className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-neutral-900 mb-2">No Versions Found</h3>
          <p className="text-neutral-600">This domain pack has no version history yet.</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-neutral-900">Version History</h2>
          <p className="text-neutral-600 mt-1">
            {versionList.total_versions} {versionList.total_versions === 1 ? 'version' : 'versions'} available
          </p>
        </div>
      </div>

      {/* Version List */}
      <div className="space-y-4">
        {versionList.versions.map((version, index) => {
          const isExpanded = expandedVersion === version.versionId;
          const isCurrent = index === 0; // First version is current

          return (
            <Card
              key={version.versionId}
              className={isCurrent ? 'border-primary-500 border-2' : ''}
            >
              <div className="cursor-pointer" onClick={() => toggleVersion(version.versionId)}>
                <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-neutral-900">
                        Version {version.versionNumber}
                      </h3>
                      {isCurrent && (
                        <Badge variant="success">Current</Badge>
                      )}
                      <Badge variant={version.status === 'active' ? 'success' : 'neutral'}>
                        {version.status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-6 text-sm text-neutral-600">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        <span>{new Date(version.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        <span>{version.createdBy}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4" />
                        <span>{version.subdomainCount} subdomains</span>
                      </div>
                    </div>
                  </div>
                  <div className="ml-4">
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 text-neutral-600" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-neutral-600" />
                    )}
                  </div>
                </div>
              </CardHeader>
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <CardBody className="border-t border-neutral-200">
                  {versionDetails ? (
                    <div className="space-y-6">
                      {/* Metadata */}
                      {versionDetails.metadata && Object.keys(versionDetails.metadata).length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-neutral-900 mb-3">Metadata</h4>
                          <div className="bg-neutral-50 rounded-lg p-4">
                            <dl className="grid grid-cols-2 gap-4">
                              {Object.entries(versionDetails.metadata).map(([key, value]) => (
                                <div key={key}>
                                  <dt className="text-sm font-medium text-neutral-600 capitalize">
                                    {key.replace(/_/g, ' ')}
                                  </dt>
                                  <dd className="text-sm text-neutral-900 mt-1">
                                    {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                  </dd>
                                </div>
                              ))}
                            </dl>
                          </div>
                        </div>
                      )}

                      {/* Subdomains */}
                      <div>
                        <h4 className="text-sm font-semibold text-neutral-900 mb-3">
                          Subdomains ({versionDetails.subdomains?.length || 0})
                        </h4>
                        <div className="space-y-2">
                          {versionDetails.subdomains && versionDetails.subdomains.length > 0 ? (
                            versionDetails.subdomains.map((subdomain: any) => (
                              <div
                                key={subdomain.subdomain_id}
                                className="flex items-start gap-3 p-3 bg-neutral-50 rounded-lg"
                              >
                                <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                                <div className="flex-1">
                                  <h5 className="font-medium text-neutral-900">{subdomain.name}</h5>
                                  {subdomain.description && (
                                    <p className="text-sm text-neutral-600 mt-1">{subdomain.description}</p>
                                  )}
                                  <div className="flex gap-2 mt-2">
                                    {subdomain.trends_count > 0 && (
                                      <Badge variant="info" size="sm">
                                        {subdomain.trends_count} trends
                                      </Badge>
                                    )}
                                    {subdomain.issues_count > 0 && (
                                      <Badge variant="warning" size="sm">
                                        {subdomain.issues_count} issues
                                      </Badge>
                                    )}
                                    {subdomain.best_practices_count > 0 && (
                                      <Badge variant="success" size="sm">
                                        {subdomain.best_practices_count} best practices
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <p className="text-sm text-neutral-600">No subdomains in this version</p>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-3 pt-4 border-t border-neutral-200">
                        <Button variant="outline" size="sm">
                          View Full Details
                        </Button>
                        {!isCurrent && (
                          <Button variant="secondary" size="sm">
                            Restore This Version
                          </Button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-primary-500" />
                      <span className="ml-3 text-neutral-600">Loading version details...</span>
                    </div>
                  )}
                </CardBody>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
};
